<template>
  <div class="createUser">
    <Loading v-if="loading" />
    <div class="createUser__header">
      <Link :title="$t('backToUsers')" @back-to="backTo" />
      <Title :title="$t('createUser')" />
    </div>
    <div class="createUser__body">
      <FormKit
        id="createUser-form"
        v-model="formData"
        type="form"
        :actions="false"
        :config="validationRules"
        @submit="submitHandler"
      >
        <DisableAutocomplete />
        <Input
          v-for="el in infoFromUser"
          :key="el.id"
          :type="el.type"
          :name="el.name"
          :label="$t(el.label)"
          :validation="el.validation"
          :data-testid="el.dataTestId"
        />
        <Input
          name="email"
          :label="$t('email')"
          :type="fieldTypes.email"
          validation="required|email"
          :validation-messages="{
            required: $t('validation.required'),
            email: $t('validation.email')
          }"
          data-testid="inputEmail"
        />
        <Dropdown
          class="createUser__body-dropdown"
          :selected-option="langsDropdown.selectedOption"
          :options="langsDropdown.options"
          :label="$t('desktopLang')"
          @select-option="selectLanguage"
        />
        <Dropdown
          class="createUser__body-dropdown"
          :label="$t('role')"
          :selected-option="userRoleDropdown.selectedOption"
          :options="userRoleDropdown.options"
          data-testid="dropdownUserRole"
          @select-option="selectRole"
        />
        <div class="createUser__body-password-title">
          <p class="title">
            {{ $t('password') }}
          </p>
        </div>
        <p class="createUser__body-message">
          {{ $t('addUserMainInfo') }}
          <br />
          {{ $t('addUserInfo') }}
        </p>
        <div class="createUser__body-password-field">
          <PasswordInput
            name="password"
            model-value="password"
            :label="$t('password')"
            :type="fieldTypes.password"
            :validation="'required|length:8'"
            :validation-messages="{
              required: $t('validation.required'),
              length: $t('validation.length8')
            }"
            @focus="handleType"
            @blur="handleType"
          />
          <PasswordInput
            name="password_confirm"
            model-value="password_confirm"
            :label="$t('confirmPassword')"
            :type="fieldTypes.password"
            :validation="'required|confirm'"
            :validation-messages="{
              required: $t('validation.required'),
              confirm: $t('validation.confirm')
            }"
            @focus="handleType"
            @blur="handleType"
          />
        </div>
      </FormKit>
    </div>
  </div>
  <Footer @clean-form="cleanForm" @submit-form="submitForm" />
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';

import Loading from '@/views/Loading.vue';

import router from '@/router';

import { infoFromUser } from '@/constants';

import { Option } from '@/types';
import { AppRole } from '@/store/modules/roles/types';

import {
  optionsLanguageEn,
  optionsLanguageFi,
  optionsLanguageSv,
  optionsLanguageNo,
  optionsLanguageLv,
  optionsLanguageSl
} from '@/services/languageService';
import { UserCreate } from '@/store/modules/users/types';

@Options({
  name: 'CreateUser',
  components: {
    Loading
  },
  data() {
    return {
      formData: {
        firstName: '',
        lastName: '',
        email: '',
        appLanguage: '',
        password: '',
        password_confirm: '',
        role: '',
        pharmacyId: ''
      },
      userRoleDropdown: {
        selectedOption: {
          value: '',
          label: ''
        },
        options: []
      },
      fieldTypes: {
        password: 'password',
        email: 'text'
      },
      langsDropdown: {
        selectedOption: [],
        options: []
      },
      validationRules: {},
      infoFromUser,
      loading: false
    };
  },
  created() {
    this.show();
    this.initLanguage();
  },
  computed: {
    ...mapState('roles', ['appRoles']),
    ...mapState('account', ['user'])
  },
  methods: {
    ...mapActions('users', ['createUser']),
    ...mapActions('roles', ['getAppRoles']),
    async show() {
      this.loading = true;
      await this.getAppRoles();

      this.initUserRole();
      this.loading = false;
    },
    initLanguage() {
      const optionsMappings: { [key: string]: Option[] } = {
        en: optionsLanguageEn,
        fi: optionsLanguageFi,
        sv: optionsLanguageSv,
        no: optionsLanguageNo,
        lv: optionsLanguageLv,
        sl: optionsLanguageSl
      };

      const userLanguage = this.user?.appLanguage;

      if (userLanguage && optionsMappings[userLanguage]) {
        this.langsDropdown.options = optionsMappings[userLanguage];
        this.langsDropdown.selectedOption = optionsMappings[userLanguage][0];
      }
    },
    async submitHandler(data: UserCreate) {
      this.loading = true;

      const payload = {
        ...data,
        appLanguage: this.langsDropdown.selectedOption.value,
        role: this.userRoleDropdown.selectedOption.value,
        pharmacyId: this.user.pharmacyId
      };

      delete payload.password_confirm;

      await this.createUser(payload);

      this.$formkit.reset('createUser-form');
      this.langsDropdown.selectedOption = [];

      router.push({ name: 'Users' });

      this.loading = false;
    },
    // eslint-disable-next-line
    handleType(event: any) {
      const { srcElement, type } = event;
      const { name, value } = srcElement;

      if (type === 'blur' && !value) {
        this.fieldTypes[name] = 'text';
      } else {
        this.fieldTypes[name] = name === 'email' ? name : 'password';
      }
    },
    backTo() {
      router.push({ name: 'Users' });
    },
    initUserRole() {
      const roles: Record<string, unknown> = {
        main_user: this.$t('mainUser'),
        user: this.$t('user'),
        janitor: this.$t('janitor')
      };

      this.userRoleDropdown.options = this.appRoles.map((i: AppRole) => ({
        value: i.value,
        label: roles[i.value]
      }));
    },
    selectRole(option: Option) {
      this.userRoleDropdown.selectedOption = option;
    },
    selectLanguage(option: Option) {
      this.langsDropdown.selectedOption = option;
    },
    submitForm() {
      this.$formkit.submit('createUser-form');
    },
    cleanForm() {
      this.$formkit.reset('createUser-form');
    }
  },
  watch: {
    user() {
      if (this.user) {
        this.initLanguage();
      }
    },
    'userRoleDropdown.selectedOption': {
      immediate: true,
      handler() {
        const roleLabel = this.userRoleDropdown.selectedOption.value;
        this.formData.role = roleLabel || '';
      }
    }
  }
})
export default class CreateUserComponent extends Vue {}
</script>

<style lang="scss">
.createUser {
  height: calc(100vh - 40px);
  padding: 20px;
  padding-bottom: 0;
  overflow-y: scroll;
  overflow-x: hidden;
  .createUser__header {
    .title {
      margin-top: 27px;
    }
  }
  .createUser__body {
    padding-top: 32px;
    .formkit-form {
      display: flex;
      flex-direction: column;
      .input {
        width: 377px;
      }
      .createUser__body-dropdown {
        width: 377px;
        height: 48px;
        margin-bottom: 30px;
      }
      .createUser__body-password-title {
        position: relative;
        margin: 10px 0;
        .title {
          font-family: 'Ubuntu-Medium';
          font-size: $sixteenSize;
          color: $main-text;
        }
        &::after {
          content: '';
          position: absolute;
          top: 9px;
          left: 80px;
          width: 100%;
          height: 1px;
          background: $ligher-grey;
        }
        &::before {
          content: '';
          position: absolute;
          top: 7px;
          left: 80px;
          width: 5px;
          height: 5px;
          border-radius: 8px;
          background: $ligher-grey;
        }
      }
      .createUser__body-message {
        color: $label;
        font-size: $fourteenSize;
        line-height: 21px;
      }
      .createUser__body-password-field {
        width: 377px;
        margin-top: 30px;
        margin-bottom: 30px;
      }
    }
    .formkit-outer {
      width: 377px;
      .formkit-options {
        display: flex;
        .formkit-option {
          margin: 0 20px;
        }
      }
    }
  }
}
</style>
